import { AfterViewChecked, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { AppConstants } from '@core-lib/config/app.constants';
import { IUser } from '@core-lib/models/user.model';
import { UploadDocumentModalComponent } from '@core-lib/modules/document-ged/components/upload-document-modal/upload-document-modal.component';
import { CustomValidators, StoreLoader, Unsubscriber } from '@core-lib/classes';
import { ActionType, StatutOperation } from '@core-lib/enums';
import { Frais, Virements } from '@core-lib/enums/coordonnes-bancaires.enum';
import { markFormGroupTouched } from '@core-lib/functions';
import { IConfirmModalConfig, ICoordonneeBancaire, IDevise } from '@core-lib/models';
import { ConfirmationService } from '@core-lib/services/confirmation-service.service';
import { AlfrescoDocumentStoreActions, AlfrescoDocumentStoreSelectors } from '@core-lib/store/alfresco-documents-store';
import { SharedStoreSelectors } from '@core-lib/store/shared-store';
import { UserStoreSelectors } from '@core-lib/store/user-store';
import { Store } from '@ngrx/store';
import { ModalRef, ModalService } from '@core-lib/modules/modal-container';
import { map, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'coordonnees-bancaires',
    templateUrl: './coordonnees-bancaires.component.html',
    styles: [],
    standalone: false
})
export class CoordonneesBancairesComponent extends Unsubscriber implements OnInit, AfterViewChecked {
    @ViewChild(UploadDocumentModalComponent) uploadDocumentModal: UploadDocumentModalComponent;

    @Input() compteId: string;
    // Bug 19012: le parent (rib-compte-pro) affiche le compteur de PJ depuis le store : il le recharge
    // et le reinitialise lui-meme, la popin ne doit pas le vider a sa fermeture.
    @Input() resetAlfrescoDocsOnDestroy: boolean = true;
    // RIB par défaut du compte (préférences) : le libellé complémentaire n'y est pas obligatoire,
    // il ne l'est que lors de la saisie d'un virement.
    @Input()
    set editCBCompte(value: boolean) {
        this._editCBCompte = value;
        if (this.form) {
            this.updateLibelleComplementaireValidators();
        }
    }
    private _editCBCompte: boolean = false;
    get editCBCompte(): boolean {
        return this._editCBCompte;
    }
    @Input() addAutomaticCBForCompte: string;
    @Input() addAutomaticCBForTiers: string;
    
    @Input()
    set coordonneesBancaires(value: ICoordonneeBancaire) {
        if (value) {
            this._coordonneesBancaires = value;
            if (value?.id) {
                this.isCreationMode = false;
                // recuperer les ficheirs du coordonnées bancaires à partir du alfresco
                this.store$.dispatch(AlfrescoDocumentStoreActions.LoadCoordonneesBancairesDocsRequest(value?.retraitId, value?.id, this.compteId));
            }
            this.bindCBWithForm();
            this.disableFields();
            this.handleAdresseBeneficiareTextArea();
            this.handleAdresseBanqueTextArea();

            if (this.isBO) {
                this.documentsCB.clearValidators();
                this.documentsCB.updateValueAndValidity();
            }
        }
    }
    private _coordonneesBancaires: ICoordonneeBancaire;
    get coordonneesBancaires(): ICoordonneeBancaire {
        return this._coordonneesBancaires;
    }
    private libelleComplementaire: string;
    @Input()
    set structure(value: string) {
        if (value) {
            this._structure = 'CARPA - ' + value.toUpperCase();
            this.libelleComplementaire ='CARPA - ' + value?.replace(AppConstants.REGEX.filterNomEmetteur, '').toUpperCase() + ' ';
            this.libelleComplementaireField.patchValue(this.libelleComplementaire);
        }
    }
    private _structure: string;
    get structure(): string {
        return this._structure;
    }

    @Input()
    set isConsultMode(value: boolean) {
        if (value) {
            this._isConsultMode = value;
            this.canAddDocument = false;
            this.checkPaysVide();
            this.form.disable({ emitEvent: false });
        }
    }
    private _isConsultMode: boolean = false;
    get isConsultMode(): boolean {
        return this._isConsultMode;
    }

    @Input()
    set deviseReference(value: string) {
        if (value) {
            this._deviseReference = value;
            this.deviseReferenceIdField.patchValue(value, { emitEvent: false });
            if (!this._isConsultMode) {
                this.deviseCompteCrediteIdField.patchValue(value, { emitEvent: false });
                this.lastSelectedDeviseCompteCredite = value;
            }

            if (this.deviseReferenceCompte && value != this._deviseReferenceCompte) {
                this.isSepaZone = false;
                if (!this.coordonneesBancaires) {
                    this.selectedPay = null;
                    this.paysBanqueBeneficaireIdField.patchValue(null);
                }
            }
        }
    }
    private _deviseReference: string;
    get deviseReference(): string {
        return this._deviseReference;
    }

    @Input()
    set deviseReferenceCompte(value: string) {
        if (value) {
            this._deviseReferenceCompte = value;

            if (this.deviseReference && value != this._deviseReference) {
                this.isSepaZone = false;
                if (!this.coordonneesBancaires) {
                    this.selectedPay = null;
                    this.paysBanqueBeneficaireIdField.patchValue(null);
                }
            }
        }
    }
    private _deviseReferenceCompte: string;
    get deviseReferenceCompte(): string {
        return this._deviseReferenceCompte;
    }

    private _isBO: boolean;
    get isBO(): boolean {
        return this._isBO;
    }
    @Input()
    set isBO(value: boolean) {
        this._isBO = value;
        if (value == true) {
            this.clearDocsCBValidators();
        }
        else {
            this.addDocsCBValidators();
        }
    }

    @Input() operationStatus: string;
    @Input() utilisateurAffecteId: string;
    @Input() saisieFrontOffice: boolean;
    @Input() filesToDeleteIds: string[];
    @Input() IsPlacements: boolean; // Vérifier si le coordonnées bancaires sont pour une affaire placement	

    isCreationMode: boolean = true;
    isSepaZone: boolean = true;
    // US 17xxx: passe a true une fois le binding initial (bindCBWithForm) termine, pour distinguer
    // les patchValue de binding des modifications reellement faites par l'utilisateur.
    private hasBoundCB: boolean = false;
    // US 17467: passe a true quand l'utilisateur modifie l'IBAN/n° de compte d'un RIB pré-rempli
    // automatiquement, pour masquer le message "RIB complété automatiquement" devenu obsolète.
    ribAutoFillEdited: boolean = false;
    // Bug 19012: PJ enregistrees supprimees dans la popin du RIB du compte, transmises au parent a
    // l'enregistrement (validateCB) ; fermer la popin sans enregistrer les conserve.
    private alfrescoDocsToDeleteIds: string[] = [];

    @ViewChild('iban') iban: ElementRef;

    public validateCB: EventEmitter<any> = new EventEmitter();
    public closeCB: EventEmitter<any> = new EventEmitter();
    form: UntypedFormGroup;

    pays$ = this.store$.select(SharedStoreSelectors.selectPays);
    paysList = [];
    selectedPay = null;
    devises$ = this.store$.select(SharedStoreSelectors.selectDevises);
    groupesCodeEconomique$ = this.store$.select(SharedStoreSelectors.selectGroupesCodeEconomique);
    groupesCodeEconomiqueList = [];
    currentUser: IUser;
    textAreaAdresseBenefeciareRows = 1;
    textAreaAdresseBanqueRows = 1;
    @Input() typeCoordBancaire: string = "";

    alfrescoDocuments$ = this.store$.select(AlfrescoDocumentStoreSelectors.selectCoordonnesBancairesDocs);
    alfrescoDocumentsLoader = new StoreLoader(
        this.store$.select(AlfrescoDocumentStoreSelectors.selectCoordonnesBancairesDocsIsLoading),
        this.store$.select(AlfrescoDocumentStoreSelectors.selectCoordonnesBancairesDocsError)
    );

    // @Output() deleteAlfrescoCBFile: EventEmitter<string> = new EventEmitter();
    @Output() decrementAlfrescoCBFile: EventEmitter<string> = new EventEmitter();

    canAddDocument: boolean = true;

    frais = Frais;
    fraisList = Object.keys(Frais);
    deviseStructureCompte: IDevise;

    get idField(): AbstractControl {
        return this.form.get('id');
    }

    get deviseReferenceIdField(): AbstractControl {
        return this.form.get('deviseReferenceId');
    }

    get paysBanqueBeneficaireIdField(): AbstractControl {
        return this.form.get('paysBanqueBeneficaireId');
    }

    get ibanBeneficiaireField(): AbstractControl {
        return this.form.get('ibanBeneficiaire');
    }

    get codeEconomiqueIdField(): AbstractControl {
        return this.form?.get('codeEconomiqueId');
    }

    get libelleComplementaireField(): AbstractControl {
        return this.form.get('libelleComplementaire');
    }

    get documentsCB(): AbstractControl {
        return this.form.get('documents');
    }
    get isIbanCBField(): AbstractControl {
        return this.form?.get('isIbanCB');
    }

    get isSepaZoneField(): AbstractControl {
        return this.form?.get('isSepaZone');
    }

    get numeroCompteField(): AbstractControl {
        return this.form?.get('numeroCompte');
    }

    get deviseCompteCrediteIdField(): AbstractControl {
        return this.form?.get('deviseCompteCrediteId');
    }

    get adresseBeneficiaireField(): AbstractControl {
        return this.form?.get('adresseBeneficiaire');
    }

    get paysBeneficaireIdField(): AbstractControl {
        return this.form?.get('paysBeneficaireId');
    }

    get nomBanqueField(): AbstractControl {
        return this.form?.get('nomBanque');
    }

    get adresseBanqueField(): AbstractControl {
        return this.form?.get('adresseBanque');
    }

    get swiftBicField(): AbstractControl {
        return this.form?.get('swiftBic');
    }

    get abaRoutingNumberField(): AbstractControl {
        return this.form?.get('abaRoutingNumber');
    }

    get fraisField(): AbstractControl {
        return this.form?.get('frais');
    }

    get saisieVirementManuscritField(): AbstractControl {
        return this.form?.get('saisieVirementManuscrit');
    }

    get virementUrgentField(): AbstractControl {
        return this.form?.get('virementUrgent');
    }

    get virementTargetField(): AbstractControl {
        return this.form?.get('virementTarget');
    }

    lastSelectedDeviseCompteCredite: string;

    constructor(
        public modalRef: ModalRef,
        private modalService: ModalService,
        private store$: Store<any>,
        private changeDetectorRef: ChangeDetectorRef,
        public ref: ElementRef,
        private confirmationService: ConfirmationService
    ) {
        super();
    }

    ngOnInit(): void {
        this.store$
            .select(UserStoreSelectors.selectUser)
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(user => {
                this.currentUser = user;
            });
        //this.uploadDocumentModal.documentType = "coordonnees-bancaires";
        this.initPaysData();
        this.buildForm();
        this.setValidators();
    }

    // For resolved Expression has changed after it was checked. Previous value: 'ng-valid: true'.
    ngAfterViewChecked(): void {
        this.changeDetectorRef.detectChanges();
    }

    setValidators(): void {
        this.adresseBeneficiaireField.valueChanges
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(() => this.updatePaysBeneficiaireValidators());

        this.paysBeneficaireIdField.valueChanges
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(() => this.updateAdresseBeneficiaireValidators());

        this.paysBanqueBeneficaireIdField.valueChanges
            .pipe(
                map(_ => this.paysBanqueBeneficaireIdField.value),
                takeUntil(this.destroySubscriptions$)
            ) 
            .subscribe(value => {
                this.resetFieldsValue();
                if (value) {
                    this.selectedPay = this.paysList.find(x => x.id === value);
                    this.getGroupesCodeEconomique();
                    this.codeEconomiqueIdField.patchValue(this.selectedPay?.codeEconomiqueId);
                    this.selectedPay?.codeEconomiqueModifiable ? this.codeEconomiqueIdField.enable() : this.codeEconomiqueIdField.disable();
                    this.selectedPay?.codeEconomiqueModifiable
                        ? this.codeEconomiqueIdField.setValidators(Validators.required)
                        : this.codeEconomiqueIdField.clearValidators();
                    // RG metier: Si le compte cabinet est dans une devise autre que la devise de référence seule la popin des virements étrangers  doit s'afficher et ce qq soit le pays sélectionné qu'il soit déclaré dans le paramétrage des pays comme sepa à oui ou non.
                    this.isSepaZone = this.deviseReferenceCompte == this.deviseReference && this.selectedPay?.zoneSEPA;
                    if (this.IsPlacements && this.isBO) {
                        if (!this.selectedPay?.zoneSEPA) {
                            const config: IConfirmModalConfig = {
                                canConfirm: false,
                                textsKey: 'ZoneHorsSepaModal',
                                cancelBtnText: 'Actions.Close',
                                modalOptions: {
                                    class: 'modal-md'
                                }
                            };
                            this.confirmationService.openConfirmationModal(config, null, () => this.paysBanqueBeneficaireIdField.patchValue(null));
                        }
                    }
                    this.isSepaZoneField.patchValue(this.isSepaZone);
                    this.isSepaZone ? this.isIbanCBField.clearValidators() : this.isIbanCBField.setValidators(Validators.required);
                    this.isSepaZone
                        ? this.deviseCompteCrediteIdField.clearValidators()
                        : this.deviseCompteCrediteIdField.setValidators(Validators.required);
                    !this.isSepaZone && this.selectedPay?.adresseBeneficiaireObligatoire
                        ? this.adresseBeneficiaireField.setValidators(CustomValidators.notEmpty())
                        : this.adresseBeneficiaireField.clearValidators();
                    !this.isSepaZone && this.selectedPay?.adresseBeneficiaireObligatoire
                        ? this.paysBeneficaireIdField.setValidators(Validators.required)
                        : this.paysBeneficaireIdField.clearValidators();
                    this.isSepaZone
                        ? this.nomBanqueField.clearValidators()
                        : this.nomBanqueField.setValidators(CustomValidators.notEmpty());
                    this.isSepaZone
                        ? this.adresseBanqueField.clearValidators()
                        : this.adresseBanqueField.setValidators(CustomValidators.notEmpty());
                    !this.isSepaZone || this.deviseReferenceCompte != this.deviseReference
                        ? this.swiftBicField.setValidators(CustomValidators.notEmpty())
                        : this.swiftBicField.clearValidators();
                    !this.isSepaZone && this.selectedPay?.codeABA
                        ? this.abaRoutingNumberField.setValidators([CustomValidators.notEmpty(), CustomValidators.exactLength(9)])
                        : this.abaRoutingNumberField.clearValidators();
                    this.updateValueAndValidity();
                } else {
                    this.clearAllFields();
                }
            });

        this.isIbanCBField.valueChanges
            .pipe(
                map(_ => this.isIbanCBField.value),
                takeUntil(this.destroySubscriptions$)
            )
            .subscribe(value => {
                if (value != null) {
                    if (value) {
                        this.ibanBeneficiaireField.setValidators(Validators.required);
                        this.numeroCompteField.clearValidators();
                        this.numeroCompteField.updateValueAndValidity();
                    } else if (!value && !this.isSepaZone) {
                        this.numeroCompteField.enable();
                        this.numeroCompteField.setValidators(Validators.required);
                        this.ibanBeneficiaireField.clearValidators();
                        this.ibanBeneficiaireField.updateValueAndValidity();
                    }
                } else {
                    this.numeroCompteField.disable();
                }
            });


        this.libelleComplementaireField.valueChanges
            .pipe(
                map(_ => this.libelleComplementaireField.value),
                takeUntil(this.destroySubscriptions$)
            )
            .subscribe(() => {
                this.updateLibelleComplementaireValidators();
                this.libelleComplementaireField.markAsTouched({});
                this.libelleComplementaireField.markAsDirty();
            });


        // US 17467: RG1 - si l'utilisateur modifie l'IBAN/n° de compte d'un RIB pré-rempli automatiquement,
        // le justificatif deja associe ne correspond plus a la saisie.
        [this.ibanBeneficiaireField, this.numeroCompteField].forEach(field => {
            field.valueChanges.pipe(takeUntil(this.destroySubscriptions$)).subscribe(() => this.clearStaleDocumentIfAutoFilled());
        });

        // Si les coordonnes bancaires ont des fichiers déjà enregistrée, l'upload de fichier n'est plus obligatoire
        this.alfrescoDocuments$.subscribe(documents => {
            if (documents?.length > 0) {
                // If we soft delete documents from upload-doc-modal , we need to hide our docs in cordonnee bancaire
                if (this.filesToDeleteIds && this.filesToDeleteIds.length > 0) {
                    this.alfrescoDocuments$ = this.alfrescoDocuments$.pipe(
                        map(x => x.filter(x => this.filesToDeleteIds.indexOf(x.id) === -1))
                    );
                    if (documents.filter(doc => !this.filesToDeleteIds.includes(doc.id)).length > 0) {
                        this.clearDocsCBValidators();
                    }
                } else {
                    this.clearDocsCBValidators();
                }
            }

            // If we delete all alfresco documents and dont add new temp document, we will emit error
            if (!this.isBO && documents && documents?.length === 0 && !this.documentsCB.value) {
                this.addDocsCBValidators();
            }
        });
    }

    // Adresse et pays du bénéficiaire ne sont saisissables qu'hors zone SEPA : une CB pré-remplie (ex. RIB du
    // tiers enregistré en BO, qui renseigne le pays sans l'adresse) ne doit pas bloquer un formulaire SEPA sur
    // des champs masqués.
    private updatePaysBeneficiaireValidators(): void {
        const adresse = this.adresseBeneficiaireField.value;
        if (!this.isSepaZone && adresse != null && adresse != '') {
            this.paysBeneficaireIdField.setValidators(Validators.required);
        } else if (this.isSepaZone || !this.selectedPay?.adresseBeneficiaireObligatoire) {
            this.paysBeneficaireIdField.clearValidators();
        }
        this.paysBeneficaireIdField.markAsTouched({});
        this.paysBeneficaireIdField.markAsDirty();
        this.paysBeneficaireIdField.updateValueAndValidity({ emitEvent: false });
    }

    private updateAdresseBeneficiaireValidators(): void {
        if (!this.isSepaZone && this.paysBeneficaireIdField.value != null) {
            this.adresseBeneficiaireField.setValidators(Validators.required);
        } else if (this.isSepaZone || !this.selectedPay?.adresseBeneficiaireObligatoire) {
            this.adresseBeneficiaireField.clearValidators();
        }
        this.adresseBeneficiaireField.markAsTouched({});
        this.adresseBeneficiaireField.markAsDirty();
        this.adresseBeneficiaireField.updateValueAndValidity({ emitEvent: false });
    }

    private updateLibelleComplementaireValidators(): void {
        const value: string = this.libelleComplementaireField.value;
        this.libelleComplementaireField.clearValidators();
        if (!this.editCBCompte && value != null && value !== '') {
            const chainesaisie = value.substring(this.libelleComplementaire?.length);
            const filteredValue = chainesaisie.replace(/[\s.]/g, ''); // Supprime les espaces et points
            if (filteredValue.length < 3) {
                this.libelleComplementaireField.addValidators([Validators.minLength(140)]);
            }
        }
        this.libelleComplementaireField.updateValueAndValidity({ emitEvent: false });
    }

    private clearAllFields(): void {
        this.codeEconomiqueIdField.patchValue(null);
        this.selectedPay = null;
        // RG metier: Si le compte cabinet est dans une devise autre que la devise de référence seule la popin des virements étrangers  doit s'afficher et ce qq soit le pays sélectionné qu'il soit déclaré dans le paramétrage des pays comme sepa à oui ou non.
        this.isSepaZone = this.deviseReferenceCompte == this.deviseReference;
        this.clearValidators();
    }

    resetFieldsValue(): void {
        this.isIbanCBField.patchValue(null);
        this.ibanBeneficiaireField.patchValue(null);
        this.numeroCompteField.patchValue(null);
        this.libelleComplementaireField.patchValue(this.structure?.toUpperCase() + ' ');
        this.documentsCB.patchValue([]);
        this.adresseBeneficiaireField.patchValue(null);
        this.paysBeneficaireIdField.patchValue(null);
        this.nomBanqueField.patchValue(null);
        this.adresseBanqueField.patchValue(null);
        this.swiftBicField.patchValue(null);
        this.abaRoutingNumberField.patchValue(null);
        this.codeEconomiqueIdField.patchValue(null);
        this.fraisField.patchValue(Frais.Beneficiaire);
        this.saisieVirementManuscritField.patchValue(false);
        this.virementUrgentField.patchValue(false);
        this.virementTargetField.patchValue(false);
    }

    isAdresseBeneficiaireRequired(): boolean {
        return this.selectedPay?.adresseBeneficiaireObligatoire ||
            (this.paysBeneficaireIdField?.value != null && this.paysBeneficaireIdField?.value != '');
    }

    isPaysBeneficaireRequired(): boolean {
        return this.selectedPay?.adresseBeneficiaireObligatoire ||
            (this.adresseBeneficiaireField?.value != null && this.adresseBeneficiaireField?.value != '');
    }

    clearValidators(): void {
        this.isIbanCBField.clearValidators();
        this.deviseCompteCrediteIdField.clearValidators();
        this.adresseBeneficiaireField.clearValidators();
        this.paysBeneficaireIdField.clearValidators();
        this.nomBanqueField.clearValidators();
        this.adresseBanqueField.clearValidators();
        this.swiftBicField.clearValidators();
        this.abaRoutingNumberField.clearValidators();
        this.libelleComplementaireField.clearAsyncValidators();
    }

    private clearDocsCBValidators(): void {
        this.documentsCB.clearValidators();
        this.documentsCB.updateValueAndValidity();
    }

    private addDocsCBValidators(): void {
        this.documentsCB.setValidators([CustomValidators.documentRequired()]);
        this.documentsCB.updateValueAndValidity();
    }

    // US 17467: RG1 - si l'utilisateur modifie l'IBAN/n° de compte d'un RIB pré-rempli automatiquement
    // (RIB du compte ou du tiers, FO ou BO), la PJ deja associee ne correspond plus a la saisie et est
    // retiree ; le message "document obligatoire" ne s'affiche que sur le FO (pas de section documents en BO).
    private clearStaleDocumentIfAutoFilled(): void {
        if (!this.hasBoundCB || !(this.addAutomaticCBForCompte || this.addAutomaticCBForTiers)) {
            return;
        }
        this.ribAutoFillEdited = true;
        if (this.documentsCB.value?.length > 0) {
            this.documentsCB.patchValue([]);
        }
        if (!this.isBO) {
            this.addDocsCBValidators();
            this.documentsCB.markAsDirty();
            this.documentsCB.markAsTouched();
        }
    }

    updateValueAndValidity(): void {
        this.adresseBeneficiaireField.updateValueAndValidity();
        this.paysBeneficaireIdField.updateValueAndValidity();
        this.isIbanCBField.updateValueAndValidity({ emitEvent: false });
        this.nomBanqueField.updateValueAndValidity();
        this.adresseBanqueField.updateValueAndValidity();
        this.swiftBicField.updateValueAndValidity();
        this.abaRoutingNumberField.updateValueAndValidity();
        this.codeEconomiqueIdField.updateValueAndValidity();
        this.libelleComplementaireField.updateValueAndValidity();
    }

    onChangeIbanCB(): void {
        this.ibanBeneficiaireField.patchValue(null);
        this.numeroCompteField.patchValue(null);
    }

    /**
     * Detect change on devise Compte Credit
     * @param deviseId
     */
    onChangeDeviseCompteCredite(deviseId): void {
        if (deviseId) {
            let selectedDevise = '';
            this.devises$.subscribe(res => (selectedDevise = res.find(x => x.id == deviseId)?.libelle));
            this.openConfirmationModal(
                'ConfirmChangeDeviseInCBModal',
                { selectedDevise: selectedDevise },
                () => (this.lastSelectedDeviseCompteCredite = deviseId),
                () =>
                    this.deviseCompteCrediteIdField.patchValue(
                        this.lastSelectedDeviseCompteCredite ? this.lastSelectedDeviseCompteCredite : this.deviseReference,
                        { emitEvent: false }
                    )
            );
        } else {
            this.deviseCompteCrediteIdField.patchValue(null, { emitEvent: false });
            this.lastSelectedDeviseCompteCredite = null;
        }
    }

    /**
     * init CB form
     */
    buildForm(): void {
        this.form = new UntypedFormGroup({
            deviseReferenceId: new UntypedFormControl({ value: null, disabled: true }, Validators.required),
            paysBanqueBeneficaireId: new UntypedFormControl(this.selectedPay !== null ? this.selectedPay?.id : null, Validators.required),
            ibanBeneficiaire: new UntypedFormControl(null),
            codeEconomiqueId: new UntypedFormControl({
                value: this.selectedPay?.codeEconomiqueId,
                disabled: !this.selectedPay?.codeEconomiqueModifiable
            }),
            libelleComplementaire: new UntypedFormControl(this.structure?.toUpperCase()),
            id: new UntypedFormControl(null),
            documents: new UntypedFormControl([], this.isBO ? [] : CustomValidators.documentRequired()),
            isIbanCB: new UntypedFormControl(null, !this.isSepaZone ? Validators.required : null),
            numeroCompte: new UntypedFormControl(null),
            deviseCompteCrediteId: new UntypedFormControl(null),
            adresseBeneficiaire: new UntypedFormControl(null),
            paysBeneficaireId: new UntypedFormControl(null),
            nomBanque: new UntypedFormControl(null),
            adresseBanque: new UntypedFormControl(null),
            swiftBic: new UntypedFormControl(null),
            abaRoutingNumber: new UntypedFormControl(null),
            frais: new UntypedFormControl(Frais.Beneficiaire),
            saisieVirementManuscrit: new UntypedFormControl(false),
            virementUrgent: new UntypedFormControl(false),
            virementTarget: new UntypedFormControl(false),
            isSepaZone: new UntypedFormControl(this.isSepaZone)
        });
    }

    initPaysData(): void {
        this.pays$.subscribe(res => {
            this.paysList = res;
            this.selectedPay = res?.find(x => x.codeISO === 'FR');
            this.isSepaZone = this.selectedPay?.zoneSEPA;
            if (this.selectedPay != null) {
                this.getGroupesCodeEconomique();
            }
        });
    }

    getGroupesCodeEconomique(codeEconomiqueId = null): void {
        this.groupesCodeEconomique$.subscribe(groupe => {
            this.groupesCodeEconomiqueList = [];
            groupe.forEach(element => {
                if(codeEconomiqueId !== null){
                    return this.groupesCodeEconomiqueList = element.liste.filter(el => el.id === codeEconomiqueId);
                }
                
                if (element.codeListeCodeEconomique === this.selectedPay?.codeListeCodeEconomique) {
                    this.groupesCodeEconomiqueList = element.liste;
                }
            });
        });
    }

    /**
     * patch form with model
     */
    bindCBWithForm(): void {
        this.hasBoundCB = false;
        this.selectedPay = this.paysList.find(x => x.id === this.coordonneesBancaires?.paysBanqueBeneficaireId);
        this.isSepaZone = this.deviseReferenceCompte == this.deviseReference && this.selectedPay?.zoneSEPA;
        this.paysBanqueBeneficaireIdField.patchValue(this.coordonneesBancaires?.paysBanqueBeneficaireId);
        this.deviseReferenceIdField.patchValue(this.deviseReference);
        this.ibanBeneficiaireField.patchValue(this.coordonneesBancaires?.ibanBeneficiaire);
        this.codeEconomiqueIdField.patchValue(this.coordonneesBancaires?.codeEconomiqueId);
        this.libelleComplementaireField.patchValue(this.coordonneesBancaires?.libelleComplementaire?.toUpperCase());
        this.idField.patchValue(this.coordonneesBancaires?.id);
        this.documentsCB.patchValue(this.coordonneesBancaires?.documents);
        this.saisieVirementManuscritField.patchValue(this.coordonneesBancaires?.saisieVirementManuscrit);
        this.virementUrgentField.patchValue(this.coordonneesBancaires?.virementUrgent);
        this.virementTargetField.patchValue(this.coordonneesBancaires?.virementTarget);
        this.numeroCompteField.patchValue(this.coordonneesBancaires?.numeroCompte);
        this.deviseCompteCrediteIdField.patchValue(this.coordonneesBancaires?.deviseCompteCrediteId);
        this.lastSelectedDeviseCompteCredite = this.coordonneesBancaires?.deviseCompteCrediteId;
        this.isIbanCBField.patchValue(
            !this.isSepaZone &&
            this.coordonneesBancaires?.ibanBeneficiaire !== null &&
            this.coordonneesBancaires?.ibanBeneficiaire !== undefined &&
            this.coordonneesBancaires?.ibanBeneficiaire !== ''
        );
        this.adresseBeneficiaireField.patchValue(this.coordonneesBancaires?.adresseBeneficiaire?.toUpperCase());
        this.paysBeneficaireIdField.patchValue(this.coordonneesBancaires?.paysBeneficaireId);
        this.nomBanqueField.patchValue(this.coordonneesBancaires?.nomBanque?.toUpperCase());
        this.adresseBanqueField.patchValue(this.coordonneesBancaires?.adresseBanque?.toUpperCase());
        this.swiftBicField.patchValue(this.coordonneesBancaires?.swiftBic?.toUpperCase());
        this.abaRoutingNumberField.patchValue(this.coordonneesBancaires?.abaRoutingNumber);
        this.fraisField.patchValue(this.coordonneesBancaires?.frais);

        this.iban?.nativeElement.focus();
        this.iban?.nativeElement.blur();

        if (
            (this.isSepaZone || (!this.isSepaZone && this.isIbanCBField.value)) &&
            this.isConsultMode &&
            this.coordonneesBancaires?.ibanBeneficiaire?.length > 0
        ) {
            const ibanValue = this.coordonneesBancaires.ibanBeneficiaire.replace(/[^\dA-Z]/g, '')
                .replace(/(.{4})/g, '$1 ')
                .trim();
            this.ibanBeneficiaireField.patchValue(ibanValue, { emitEvent: false });
        }

        if(this.coordonneesBancaires?.codeEconomiqueId !== this.selectedPay?.codeEconomique?.id){
            this.getGroupesCodeEconomique(this.coordonneesBancaires?.codeEconomiqueId)
        }
        this.hasBoundCB = true;
    }

    onClose(): void {
        if (!this.isConsultMode) {
            this.openConfirmationModal('ConfirmCBModal', null, () => { this.modalRef.hide(); this.closeCB.emit({ value: null }); });
        } else {
            this.modalRef.hide();
            this.closeCB.emit({ value: null });
        }
    }

    /**
     * ouvrir le pop up de confirmation
     */
    openConfirmationModal(textsKey: string, bodyParams?: any, onConfirm?: () => void, onCancel?: () => void) {
        const config: IConfirmModalConfig = {
            textsKey: textsKey,
            bodyParams: bodyParams,
            modalOptions: {
                class: 'modal-md'
            }
        };
        this.confirmationService.openConfirmationModal(config, onConfirm, onCancel);
    }

    /**
     * Valider les modificaions dans la pop up CB
     */
    onSubmit(): void {
        // La zone SEPA peut changer après la saisie de l'adresse ou du pays (ex. devise) : la règle est
        // réévaluée sur l'état final du formulaire.
        this.updatePaysBeneficiaireValidators();
        this.updateAdresseBeneficiaireValidators();
        if (this.uploadDocumentModal && (!this.uploadDocumentModal.onConfirmFromCB() && !this.form.valid)) {
            markFormGroupTouched(this.form as UntypedFormGroup);
            return;
        }
        if (this.form.valid) {
            this.validateCB.emit({
                type: this.isCreationMode ? ActionType.Submit : ActionType.Update,
                value: this.getModelFromForm(),
                documentsToDeleteIds: this.alfrescoDocsToDeleteIds
            });
            this.modalRef.hide();
        } else {
            markFormGroupTouched(this.form as UntypedFormGroup);
        }
    }

    onDrag($event): void {
        $event.preventDefault();
    }

    /**
     * Action pour la mise � jour de la liste des documents
     * @param files liste des fichiers
     */
    handleDocumentsSelection(files?: any): void {
        this.documentsCB.patchValue(files, { emitEvent: false });
    }

    private getModelFromForm(): ICoordonneeBancaire {
        const formValue: ICoordonneeBancaire = this.form.getRawValue();
        formValue.ibanBeneficiaire = formValue?.ibanBeneficiaire?.replace(/\s/g, '')?.toLocaleUpperCase();
        return formValue;
    }

    private canDisableFields(){
        // US 7401: RG1: Le champ « Libellé complémentaire transmis au bénéficiaire » est modifiable quelque soit l'utilisateur.  Les autres champs sont modifiables uniquement par l'utilisateur qui a enregistré les coordonnées bancaires.
        const createdByCondition = this.currentUser?.id === this.coordonneesBancaires?.utilisateurCreationId;

        // US 7401: RG3: Sur le FO, l'avocat peut modifier tous les champs de la pop in de coordonnées bancaires si l'opération est:
        // En cours de saisie(statut = vide)
        // 		Préenregistré(statut = En attente d’enregistrement par l’avocat)
        // US 7401: RG4: Sur le BO, l'utilisateur peut modifier tous les champs de la pop in de coordonnées bancaires si l'opération est au statut "En attente d’enregistrement par la carpa"
        // Si l'opération est d'origine FO: seul le champ "Libellé complémentaire" est modifiable par le BO(cf RG1)
        const conditionStatutOperation = this.isBO
            ? this.operationStatus === StatutOperation.EnAttenteEnregistrementParCarpa
            : this.operationStatus === StatutOperation.EnAttenteEnregistrementParAvocat;
            
        // US 17xxx: RG1 RIB du Cabinet: le pré-remplissage automatique ne doit pas empêcher l'avocat
        // de saisir d'autres coordonnées bancaires pour ce retrait si besoin (seul le RIB par défaut
        // du compte, modifiable via les préférences, reste hors de portée de cette pop in).
        // RIB deja present sur le tiers (addAutomaticCBForTiers): le pre-remplissage automatique ne doit pas
        // empecher l'utilisateur de verifier/modifier ces coordonnees bancaires pour ce retrait si besoin.
        return !this.editCBCompte &&
            (!this.isCreationMode && (!createdByCondition || !conditionStatutOperation || (this.isBO && this.saisieFrontOffice)));
    }

    /**
     * Action pour désactiver les champs de la modale coordonnées bancaire celon les conditions citées ci aprés
     */
    disableFields(): void {
        if (this.canDisableFields()) {
            this.paysBanqueBeneficaireIdField.disable({ emitEvent: false });
            this.deviseReferenceIdField.disable({ emitEvent: false });
            this.ibanBeneficiaireField.disable({ emitEvent: false });
            this.codeEconomiqueIdField.disable({ emitEvent: false });
            this.documentsCB.disable({ emitEvent: false });
            this.isIbanCBField.disable({ emitEvent: false });
            this.numeroCompteField.disable({ emitEvent: false });
            this.deviseCompteCrediteIdField.disable({ emitEvent: false });
            this.adresseBeneficiaireField.disable({ emitEvent: false });
            this.paysBeneficaireIdField.disable({ emitEvent: false });
            this.adresseBanqueField.disable({ emitEvent: false });
            this.nomBanqueField.disable({ emitEvent: false });
            this.swiftBicField.disable({ emitEvent: false });
            this.abaRoutingNumberField.disable({ emitEvent: false });
            this.canAddDocument = false;
            this.fraisField.disable({ emitEvent: false });
        }

        if (this.isBO && this.operationStatus !=null && this.operationStatus != StatutOperation.EnAttenteEnregistrementParCarpa) {
            this.saisieVirementManuscritField.disable({ emitEvent: false });
            this.virementUrgentField.disable({ emitEvent: false });
            this.virementTargetField.disable({ emitEvent: false });
        }
    }

    ngOnDestroy(): void {
        if (this.resetAlfrescoDocsOnDestroy) {
            this.store$.dispatch(AlfrescoDocumentStoreActions.resetAction());
        }
    }

    clearOther(type: string): void {
        switch (type) {
            case Virements.SaisieVirementManuscrit:
                this.virementUrgentField.patchValue(false);
                this.virementTargetField.patchValue(false);
                break;
            case Virements.VirementUrgent:
                this.saisieVirementManuscritField.patchValue(false);
                this.virementTargetField.patchValue(false);
                break;
            case Virements.VirementTarget:
                this.saisieVirementManuscritField.patchValue(false);
                this.virementUrgentField.patchValue(false);
                break;
            default:
                break;
        }
    }

    buildInputId(name: string): string {
        return name.replace(/\s/g, '').toLocaleLowerCase();
    }

    // onDeleteAlfrescoCBDocId(id: string): void {
    // 	this.deleteAlfrescoCBFile.emit(id);
    // }
    handleDeleteEmited(id: string): void {
        if (this.editCBCompte) {
            this.alfrescoDocsToDeleteIds.push(id);
        }
        // Bug 19012: la PJ n'est obligatoire que s'il ne reste plus aucun document (enregistre ou ajoute)
        const remainingDocsCount = (this.uploadDocumentModal?.gedDocs?.length ?? 0) + (this.documentsCB.value?.length ?? 0);
        if (this.isBO == true || remainingDocsCount > 0) {
            this.clearDocsCBValidators();
        }
        else {
            this.addDocsCBValidators();
        }
        this.decrementAlfrescoCBFile.emit(id);
    }
    updateAlfrescoCBDocs(): void {
        this.store$.dispatch(
            AlfrescoDocumentStoreActions.LoadCoordonneesBancairesDocsRequest(
                this.coordonneesBancaires?.retraitId,
                this.coordonneesBancaires?.id,
                this.compteId
            )
        );
    }

    /**
     * Issues 10125 - Pays vide ou iban vide
     */
    private isIbanBeneficiaireEmpty(): boolean {
        return !this.ibanBeneficiaireField.value || this.ibanBeneficiaireField.value === '';
    }

    private isNumeroCompteEmpty(): boolean {
        return !this.numeroCompteField.value || this.numeroCompteField.value === '';
    }

    private isPaysBanqueBeneficaireEmpty(): boolean {
        return !this.paysBanqueBeneficaireIdField.value;
    }

    private checkPaysVide(): void {
        if (this.isPaysBanqueBeneficaireEmpty()) {
            this.handleEmptyPays();
        } else {
            this.handleNonEmptyPays();
        }
    }

    private handleEmptyPays(): void {
        this.isSepaZone = false;
        if (!this.isIbanBeneficiaireEmpty() && this.isNumeroCompteEmpty()) {
            this.isSepaZone = true;
        } else if (this.isIbanBeneficiaireEmpty() && !this.isNumeroCompteEmpty()) {
            this.isIbanCBField.patchValue(false);
        }
    }

    private handleNonEmptyPays(): void {
        if (!this.isSepaZone && !this.isIbanBeneficiaireEmpty()) {
            this.isIbanCBField.patchValue(true);
        } else if (this.isIbanBeneficiaireEmpty() && !this.isNumeroCompteEmpty()) {
            this.isIbanCBField.patchValue(false);
            this.isSepaZone = false;
        }
    }

    private handleAdresseBeneficiareTextArea(): void {
        this.textAreaAdresseBenefeciareRows = this.coordonneesBancaires?.adresseBeneficiaire?.includes('\r') ? 2 : 1;
    }

    private handleAdresseBanqueTextArea(): void {
        this.textAreaAdresseBanqueRows = this._coordonneesBancaires?.adresseBanque?.includes('\r') ? 2 : 1;
    }
}
